import React, { useState } from 'react';
import { 
  AlertTriangle, AlertCircle, Bell, XCircle,
  Package, TrendingDown, Clock, Settings
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';

interface StockItem {
  skuId: string;
  skuName: string;
  category: string;
  currentStock: number;
  optimalStock: number;
  minStock: number;
  maxStock: number;
  dailyDemand: number;
  leadTimeDays: number;
  reorderPoint: number;
  orderQuantity: number;
  stockDays: number;
  status: 'optimal' | 'low' | 'critical' | 'excess';
}

interface StockAlertsProps {
  stockItems: StockItem[];
}

interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info';
  category: 'stockout' | 'excess' | 'expiry' | 'demand_spike' | 'supplier';
  title: string;
  message: string;
  skuId?: string;
  skuName?: string;
  timestamp: Date;
  isRead: boolean;
  actionRequired: boolean;
}

const StockAlerts: React.FC<StockAlertsProps> = ({ stockItems }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [alerts, setAlerts] = useState<Alert[]>(() => generateAlerts(stockItems));
  const [notificationSettings, setNotificationSettings] = useState({
    critical: true,
    warning: true,
    info: false,
    email: false,
    push: true,
    slack: false
  });

  function generateAlerts(items: StockItem[]): Alert[] {
    const generatedAlerts: Alert[] = [];
    
    // 在庫切れアラート
    items.filter(item => item.status === 'critical').forEach(item => {
      generatedAlerts.push({
        id: `alert_${item.skuId}_stockout`,
        type: 'critical',
        category: 'stockout',
        title: '在庫切れ寸前',
        message: `${item.skuName}の在庫が${item.currentStock}個まで減少。${item.stockDays}日以内に在庫切れの恐れ。`,
        skuId: item.skuId,
        skuName: item.skuName,
        timestamp: new Date(Date.now() - Math.random() * 3600000),
        isRead: false,
        actionRequired: true
      });
    });
    
    // 低在庫警告
    items.filter(item => item.status === 'low').forEach(item => {
      generatedAlerts.push({
        id: `alert_${item.skuId}_low`,
        type: 'warning',
        category: 'stockout',
        title: '低在庫警告',
        message: `${item.skuName}が再発注点に到達。現在庫: ${item.currentStock}個`,
        skuId: item.skuId,
        skuName: item.skuName,
        timestamp: new Date(Date.now() - Math.random() * 7200000),
        isRead: Math.random() > 0.5,
        actionRequired: true
      });
    });
    
    // 過剰在庫アラート
    items.filter(item => item.status === 'excess').forEach(item => {
      generatedAlerts.push({
        id: `alert_${item.skuId}_excess`,
        type: 'info',
        category: 'excess',
        title: '過剰在庫',
        message: `${item.skuName}の在庫が適正量の2倍以上。販促検討を推奨。`,
        skuId: item.skuId,
        skuName: item.skuName,
        timestamp: new Date(Date.now() - Math.random() * 14400000),
        isRead: Math.random() > 0.3,
        actionRequired: false
      });
    });
    
    // その他のアラート
    generatedAlerts.push(
      {
        id: 'alert_demand_spike',
        type: 'warning',
        category: 'demand_spike',
        title: '需要急増検知',
        message: '飲料カテゴリで前日比150%の需要増を検知。気温上昇が要因の可能性。',
        timestamp: new Date(Date.now() - 1800000),
        isRead: false,
        actionRequired: true
      },
      {
        id: 'alert_supplier_delay',
        type: 'warning',
        category: 'supplier',
        title: 'サプライヤー遅延',
        message: '東京食品卸からの配送が1日遅延の見込み。影響品目: 15品',
        timestamp: new Date(Date.now() - 3600000),
        isRead: false,
        actionRequired: true
      }
    );
    
    return generatedAlerts.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }

  const handleMarkAsRead = (alertId: string) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, isRead: true } : alert
    ));
  };

  const handleDismissAlert = (alertId: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'critical': return <XCircle size={18} style={{ color: colors.status.error }} />;
      case 'warning': return <AlertTriangle size={18} style={{ color: colors.status.warning }} />;
      default: return <AlertCircle size={18} style={{ color: colors.status.info }} />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'stockout': return <Package size={14} />;
      case 'excess': return <TrendingDown size={14} />;
      case 'expiry': return <Clock size={14} />;
      case 'demand_spike': return <AlertTriangle size={14} />;
      default: return <Bell size={14} />;
    }
  };

  const filteredAlerts = selectedCategory === 'all' 
    ? alerts 
    : alerts.filter(alert => alert.type === selectedCategory);

  const unreadCount = alerts.filter(a => !a.isRead).length;
  const criticalCount = alerts.filter(a => a.type === 'critical').length;
  const actionRequiredCount = alerts.filter(a => a.actionRequired && !a.isRead).length;

  return (
    <div className="space-y-6">
      {/* アラートサマリー */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Bell size={20} style={{ color: colors.accent.blue }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {unreadCount}件
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                未読アラート
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <XCircle size={20} style={{ color: colors.status.error }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {criticalCount}件
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                緊急アラート
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle size={20} style={{ color: colors.status.warning }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {actionRequiredCount}件
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                要対応
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Settings size={20} style={{ color: colors.text.secondary }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {Object.values(notificationSettings).filter(v => v === true).length}個
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                通知設定
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 通知設定 */}
      <Card>
        <div className="p-4 border-b" style={{ borderColor: colors.ui.border }}>
          <h3 className="font-semibold" style={{ color: colors.text.primary }}>
            通知設定
          </h3>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium mb-3" style={{ color: colors.text.primary }}>
                アラートレベル
              </h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.critical}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, critical: e.target.checked }))}
                  />
                  <span className="text-sm">緊急アラート</span>
                </label>
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.warning}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, warning: e.target.checked }))}
                  />
                  <span className="text-sm">警告アラート</span>
                </label>
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.info}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, info: e.target.checked }))}
                  />
                  <span className="text-sm">情報アラート</span>
                </label>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-3" style={{ color: colors.text.primary }}>
                通知方法
              </h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.email}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, email: e.target.checked }))}
                  />
                  <span className="text-sm">メール通知</span>
                </label>
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.push}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, push: e.target.checked }))}
                  />
                  <span className="text-sm">プッシュ通知</span>
                </label>
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.slack}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, slack: e.target.checked }))}
                  />
                  <span className="text-sm">Slack通知</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* アラートフィルター */}
      <div className="flex items-center gap-2">
        {['all', 'critical', 'warning', 'info'].map(category => (
          <Button
            key={category}
            variant={selectedCategory === category ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setSelectedCategory(category)}
          >
            {category === 'all' ? '全て' : 
             category === 'critical' ? '緊急' :
             category === 'warning' ? '警告' : '情報'}
            {category !== 'all' && (
              <Badge 
                variant="secondary" 
                size="small" 
                className="ml-1"
              >
                {alerts.filter(a => a.type === category).length}
              </Badge>
            )}
          </Button>
        ))}
      </div>

      {/* アラート一覧 */}
      <div className="space-y-3">
        {filteredAlerts.map(alert => (
          <Card 
            key={alert.id}
            className={`p-4 ${!alert.isRead ? 'border-l-4' : ''}`}
            style={{
              borderLeftColor: !alert.isRead ? (
                alert.type === 'critical' ? colors.status.error :
                alert.type === 'warning' ? colors.status.warning :
                colors.status.info
              ) : undefined,
              backgroundColor: !alert.isRead ? colors.ui.bgSecondary : 'transparent'
            }}
          >
            <div className="flex items-start gap-4">
              <div className="mt-1">
                {getAlertIcon(alert.type)}
              </div>
              
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium" style={{ color: colors.text.primary }}>
                        {alert.title}
                      </h4>
                      {getCategoryIcon(alert.category)}
                      {alert.actionRequired && (
                        <Badge variant="error" size="small">
                          要対応
                        </Badge>
                      )}
                    </div>
                    {alert.skuName && (
                      <span className="text-xs" style={{ color: colors.text.secondary }}>
                        {alert.skuId} - {alert.skuName}
                      </span>
                    )}
                  </div>
                  <span className="text-xs" style={{ color: colors.text.secondary }}>
                    {new Date(alert.timestamp).toLocaleTimeString('ja-JP')}
                  </span>
                </div>
                
                <p className="text-sm mb-3" style={{ color: colors.text.primary }}>
                  {alert.message}
                </p>
                
                <div className="flex items-center gap-2">
                  {alert.actionRequired && (
                    <Button size="small" variant="primary">
                      対応する
                    </Button>
                  )}
                  {!alert.isRead && (
                    <Button 
                      size="small" 
                      variant="secondary"
                      onClick={() => handleMarkAsRead(alert.id)}
                    >
                      既読にする
                    </Button>
                  )}
                  <Button 
                    size="small" 
                    variant="secondary"
                    onClick={() => handleDismissAlert(alert.id)}
                  >
                    削除
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default StockAlerts;
